import { useState, useEffect } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/seo/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Calculator, ExternalLink, FileDown, Send } from "lucide-react";
import { MeasureSelection } from "@/components/isde/MeasureSelection";
import { SpecificationForm } from "@/components/isde/SpecificationForm";
import { HousingDetails } from "@/components/isde/HousingDetails";
import { CalculationResult } from "@/components/isde/CalculationResult";
import { LeadForm } from "@/components/isde/LeadForm";
import { ISDEFAQSection } from "@/components/isde/ISDEFAQSection";
import { useISDECalculation } from "@/hooks/useISDECalculation";

const ISDESubsidieCalculator = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [calculationData, setCalculationData] = useState({
    category: "",
    type: "",
    value: 0,
    energyLabel: "",
    biobased: false,
    housingType: "",
    buildYear: "",
    postcode: "",
    executionDate: ""
  });

  const { calculateSubsidy, isLoading, subsidyResult } = useISDECalculation();

  const steps = [
    { id: 1, title: "Maatregel", description: "Selecteer uw verduurzamingsmaatregel" },
    { id: 2, title: "Specificaties", description: "Vul de details in" },
    { id: 3, title: "Woninggegevens", description: "Gegevens over uw woning" },
    { id: 4, title: "Resultaat", description: "Uw geschatte ISDE subsidie" }
  ];

  const progress = (currentStep / steps.length) * 100;

  const handleNext = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCalculate = async () => {
    await calculateSubsidy(calculationData);
    setCurrentStep(4);
  };

  useEffect(() => {
    // Calculate when we have enough data and reach step 4
    if (currentStep === 4 && calculationData.category && calculationData.type) {
      handleCalculate();
    }
  }, [currentStep]);

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="ISDE Subsidie Calculator 2025 - Bereken uw subsidie | SOL-VI"
        description="🧮 Bereken uw ISDE subsidie 2025 voor warmtepompen, isolatie en zonneboilers. Gratis calculator met actuele tarieven ✅ Direct resultaat en offerte aanvragen"
        keywords="ISDE subsidie calculator, subsidie berekenen 2025, warmtepomp subsidie, isolatie subsidie, ISDE tarieven, verduurzaming subsidie Gelderland"
        canonical="https://sol-vi.nl/isde-subsidie-calculator"
        type="website"
      />
      
      <Header />
      
      <main className="pt-20">
        {/* RVO Video Section */}
        <div className="bg-gradient-to-br from-primary/10 to-background py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-bold mb-4">
                  ISDE Subsidie Uitgelegd door RVO
                </h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Bekijk deze officiële uitleg van de Rijksdienst voor Ondernemend Nederland over de ISDE subsidie
                </p>
              </div>
              
              <div className="relative bg-card rounded-xl shadow-lg overflow-hidden">
                <div className="aspect-video">
                  <video 
                    className="w-full h-full object-cover"
                    controls
                    preload="metadata"
                    poster="/lovable-uploads/9b284f26-7aa6-4e4a-abbb-6a15ecc27122.png"
                  >
                    <source src="https://www.rovid.nl/rvo/rvo/2024/rvo-rvo-20240626-idsayfrb5-web.webm" type="video/webm" />
                    <p className="p-4 text-center text-muted-foreground">
                      Uw browser ondersteunt het video element niet. 
                      <a 
                        href="https://www.rovid.nl/rvo/rvo/2024/rvo-rvo-20240626-idsayfrb5-web.webm" 
                        className="text-primary hover:underline ml-2"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Bekijk de video direct
                      </a>
                    </p>
                  </video>
                </div>
                
                <div className="p-6 bg-muted/30">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-lg">Officiële RVO Uitleg</h3>
                      <p className="text-sm text-muted-foreground">
                        Investeringssubsidie Duurzame Energie (ISDE) 2024-2025
                      </p>
                    </div>
                    <Button variant="outline" size="sm" asChild>
                      <a 
                        href="https://www.rvo.nl/subsidies-financiering/isde"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2"
                      >
                        <ExternalLink className="h-4 w-4" />
                        RVO Website
                      </a>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Hero Section */}
        <div className="bg-gradient-to-br from-primary/5 via-background to-primary-glow/5 py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="flex items-center justify-center mb-6">
                <Calculator className="h-12 w-12 text-primary mr-4" />
                <h1 className="text-4xl md:text-5xl font-bold">
                  ISDE Subsidie Calculator 2025
                </h1>
              </div>
              <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
                Bereken uw ISDE subsidie voor warmtepompen, isolatie en andere verduurzamingsmaatregelen. 
                Actuele tarieven 2025 en direct resultaat.
              </p>
              
              <div className="flex flex-wrap gap-4 justify-center mb-8">
                <Badge variant="secondary" className="text-sm">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Officiële RVO tarieven 2025
                </Badge>
                <Badge variant="secondary" className="text-sm">
                  Budget beschikbaar t/m 2030
                </Badge>
                <Badge variant="secondary" className="text-sm">
                  Gratis berekening & advies
                </Badge>
              </div>
            </div>
          </div>
        </div>

        {/* Calculator Section */}
        <div className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              
              {/* Progress Bar */}
              <div className="mb-12">
                <div className="flex justify-between items-center mb-4">
                  {steps.map((step, index) => (
                    <div key={step.id} className="flex flex-col items-center">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                        step.id <= currentStep 
                          ? 'bg-primary text-primary-foreground' 
                          : 'bg-muted text-muted-foreground'
                      }`}>
                        {step.id}
                      </div>
                      <div className="text-center mt-2 hidden md:block">
                        <div className="text-sm font-medium">{step.title}</div>
                        <div className="text-xs text-muted-foreground">{step.description}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <Progress value={progress} className="h-2" />
              </div>

              {/* Calculator Steps */}
              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span className="bg-primary text-primary-foreground w-8 h-8 rounded-full flex items-center justify-center text-sm">
                      {currentStep}
                    </span>
                    {steps[currentStep - 1]?.title}
                  </CardTitle>
                  <CardDescription>
                    {steps[currentStep - 1]?.description}
                  </CardDescription>
                </CardHeader>
                
                <CardContent className="space-y-6">
                  {currentStep === 1 && (
                    <MeasureSelection
                      selectedCategory={calculationData.category}
                      selectedType={calculationData.type}
                      onSelect={(category, type) => 
                        setCalculationData(prev => ({ ...prev, category, type }))
                      }
                    />
                  )}

                  {currentStep === 2 && (
                    <SpecificationForm
                      category={calculationData.category}
                      type={calculationData.type}
                      value={calculationData.value}
                      energyLabel={calculationData.energyLabel}
                      biobased={calculationData.biobased}
                      onChange={(field, value) => 
                        setCalculationData(prev => ({ ...prev, [field]: value }))
                      }
                    />
                  )}

                  {currentStep === 3 && (
                    <HousingDetails
                      housingType={calculationData.housingType}
                      buildYear={calculationData.buildYear}
                      postcode={calculationData.postcode}
                      executionDate={calculationData.executionDate}
                      onChange={(field, value) => 
                        setCalculationData(prev => ({ ...prev, [field]: value }))
                      }
                    />
                  )}

                  {currentStep === 4 && (
                    <div className="space-y-8">
                      <CalculationResult
                        result={subsidyResult}
                        calculationData={calculationData}
                        isLoading={isLoading}
                      />
                      
                      {subsidyResult && (
                        <LeadForm
                          calculationData={calculationData}
                          subsidyResult={subsidyResult}
                        />
                      )}
                    </div>
                  )}

                  {/* Navigation Buttons */}
                  {currentStep < 4 && (
                    <div className="flex justify-between pt-6">
                      <Button 
                        variant="outline" 
                        onClick={handlePrevious}
                        disabled={currentStep === 1}
                      >
                        Vorige
                      </Button>
                      
                      <Button 
                        onClick={handleNext}
                        disabled={
                          (currentStep === 1 && (!calculationData.category || !calculationData.type)) ||
                          (currentStep === 2 && !calculationData.value) ||
                          (currentStep === 3 && (!calculationData.housingType || !calculationData.postcode))
                        }
                      >
                        {currentStep === 3 ? 'Bereken Subsidie' : 'Volgende'}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Info Cards */}
              <div className="grid md:grid-cols-3 gap-6 mt-12">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <ExternalLink className="h-5 w-5 text-primary" />
                      RVO Informatie
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground mb-4">
                      Officiële informatie en meldcodelijsten van RVO.
                    </p>
                    <Button variant="outline" size="sm" asChild>
                      <a href="https://www.rvo.nl/subsidies-financiering/isde/woningeigenaren" target="_blank" rel="noopener noreferrer">
                        Naar RVO.nl
                      </a>
                    </Button>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <FileDown className="h-5 w-5 text-primary" />
                      Stappenplan
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground mb-4">
                      Complete handleiding voor het aanvragen van ISDE subsidie.
                    </p>
                    <Button variant="outline" size="sm">
                      Download Gids
                    </Button>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Send className="h-5 w-5 text-primary" />
                      Persoonlijk Advies
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground mb-4">
                      Krijg maatwerk advies en offerte voor uw situatie.
                    </p>
                    <Button size="sm">
                      Vraag Offerte
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <ISDEFAQSection />
      </main>
      
      <Footer />
    </div>
  );
};

export default ISDESubsidieCalculator;